/**
 * mcs-parser.js — read a Music Construction Set song (.MCS / .MCD, IBM-PC 1984)
 * into the note list the chiptune engine already speaks.
 *
 * WHY THIS EXISTS
 * A user asked twice for MCS support, the second time with the thing that made
 * it possible: https://github.com/chjmartin2/MCS-Convert — an MIT-licensed,
 * disassembly-verified decoding of MCSDISK.EXE's own player, round-trip tested
 * against an 86-song corpus. The first answer here was "the format is
 * undocumented, this is weeks of reverse engineering with an unknown outcome",
 * and that was simply wrong: the hard part was already done, in public, under a
 * licence that lets us use it.
 *
 * ATTRIBUTION. The byte-level knowledge below — pitch windows, symbol dispatch,
 * the 8va and tie handlers, the tempo curve — is a port of that project's
 * `mcs_convert/mcs/reader.py` (MIT, © chjmartin2). Nothing here was independently
 * discovered. See docs/mcs-format.md upstream for the disassembly write-up.
 *
 * WHAT IT PRODUCES
 * `{ notes: [{ startTimeSeconds, durationSeconds, pitchMidi, amplitude }], … }`
 * — exactly the shape BasicPitch/midi-to-notes hand the chiptune voicer, so an
 * MCS file lands on the 2A03 channels through the same path as everything else
 * and inherits arpeggio, portamento, vibrato and MIDI export for free.
 *
 * Style: ES5 var/function, matching the rest of public/js.
 */
(function (scope) {
'use strict';

var CLEF_TREBLE = 0x06, CLEF_BASS = 0x0D;
var SYM_NATURAL = 0x0E, SYM_SHARP = 0x0F, SYM_FLAT = 0x10;
var SYM_DOT = 0x11, SYM_OCTAVA = 0x12, SYM_TIE = 0x13, SYM_TIE_BELOW = 0x19;
var BEAM_OFFSET = 0x14;
var MIDI_ANCHOR = 44;
var TEMPO_BASE_BYTE = 0x77;

/* The engine's own vertical→pitch ladders, 2×semitone per staff position, taken
   verbatim from MCSDISK.EXE. The window follows the CLEF alone — a bass clef on
   the top staff reads the bass window — and 0 marks an unusable position. */
function hexBytes(h) {
    var out = [];
    for (var i = 0; i < h.length; i += 2) out.push(parseInt(h.substr(i, 2), 16));
    return out;
}
var TREBLE_WINDOW = hexBytes('706c6866625e5a5854504e4a4642403c3836322e');   // E7 down to G4
var BASS_WINDOW   = hexBytes('4642403c3836322e2a2824201e1a1612100c080600'); // G5 down to B2

// value n → 2**n thirty-second ticks (0=32nd … 5=whole). One tick = one 32nd.
var NOTE_TICKS = [1, 2, 4, 8, 16, 32];

/** ('note'|'rest'|'', value) for a symbol. Beamed notes (0x14..0x18) dispatch to
 *  the identical duration handlers — dropping them loses whole fast runs. */
function noteValue(sym) {
    if (sym >= BEAM_OFFSET && sym <= BEAM_OFFSET + 4) return ['note', sym - BEAM_OFFSET];
    if (sym >= 0 && sym <= 5) return ['note', sym];
    if (sym >= 7 && sym <= 12) return ['rest', sym - 7];
    return ['', 0];
}

function vertical(b0, b1) { return ((b1 & 7) << 3) | (b0 >> 5); }
function symbolOf(b0)     { return b0 & 0x1F; }
function xSlot(b1)        { return b1 >> 3; }

/** Seconds per tick. Measured from DOSBox-X captures upstream: seconds per
 *  SIXTEENTH = 0.067 + 0.016·step; a tick is a 32nd, so half that. */
function tickSecondsFor(byte0) {
    var step = Math.max(0, Math.floor((byte0 - TEMPO_BASE_BYTE) / 3));
    return (0.067 + 0.016 * step) / 2;
}

/** Measures are `FF FF count prev` records; (0,0) ends a staff, (0, prev≠0) is an
 *  empty measure that must stay in place. */
function parseRecords(d) {
    var recs = [], i = 0x0F;
    while (i < d.length - 1) {
        if (d[i] === 0xFF && d[i + 1] === 0xFF) {
            var count = i + 2 < d.length ? d[i + 2] : 0;
            var prev  = i + 3 < d.length ? d[i + 3] : 0;
            var j = i + 4, entries = [];
            while (j < d.length - 1 && !(d[j] === 0xFF && d[j + 1] === 0xFF)) {
                entries.push([d[j], d[j + 1]]);
                j += 2;
            }
            recs.push({ count: count, prev: prev, entries: entries });
            i = j;
        } else { i++; }
    }
    return recs;
}

function splitStaves(recs) {
    var staves = [], cur = [];
    for (var i = 0; i < recs.length; i++) {
        var r = recs[i];
        if (r.count === 0 && r.prev === 0) {
            if (cur.length) { staves.push(cur); cur = []; }
            continue;
        }
        cur.push(r);
    }
    if (cur.length) staves.push(cur);
    return staves;
}

function staffMidi(staff, v, acc2, clef) {
    var win = ((clef || staff.clef) === CLEF_TREBLE) ? TREBLE_WINDOW : BASS_WINDOW;
    var idx = v - staff.vBase;
    if (idx < 0 || idx >= win.length || win[idx] === 0) return 0;
    return Math.floor((win[idx] + acc2 + staff.octava) / 2) + MIDI_ANCHOR;
}

/** The first record of a staff is its clef record: clef glyph, key signature and
 *  a possible whole-staff 8va. vBase is POSITIONAL (which screen staff the notes
 *  sit on); the pitch window follows the clef. */
function readStaff(recs) {
    var clef = CLEF_TREBLE, keysig = [0, 0, 0, 0, 0, 0, 0], octava = 0;
    if (recs.length && recs[0].entries.length) {
        for (var e = 0; e < recs[0].entries.length; e++) {
            var b0 = recs[0].entries[e][0], b1 = recs[0].entries[e][1];
            var sym = symbolOf(b0);
            if (sym === CLEF_TREBLE || sym === CLEF_BASS) clef = sym;
            else if (sym === SYM_NATURAL || sym === SYM_SHARP || sym === SYM_FLAT) {
                var acc = sym === SYM_SHARP ? 2 : (sym === SYM_FLAT ? -2 : 0);
                keysig[(vertical(b0, b1) - 1) % 7] = acc;
            } else if (sym === SYM_OCTAVA) octava = 24;
        }
    }
    var high = 0, total = 0;
    for (var r = 1; r < recs.length; r++) {
        for (var k = 0; k < recs[r].entries.length; k++) {
            var e0 = recs[r].entries[k][0], e1 = recs[r].entries[k][1];
            if (noteValue(symbolOf(e0))[0] !== 'note') continue;
            total++;
            if (vertical(e0, e1) >= 21) high++;
        }
    }
    var vBase = total ? (high * 2 >= total ? 21 : 1) : (clef === CLEF_BASS ? 21 : 1);
    return { clef: clef, vBase: vBase, keysig: keysig, octava: octava, records: recs.slice(1) };
}

/** One measure → rhythmic slots. A chord shares an x slot and advances by its
 *  SHORTEST member while longer members keep ringing. */
function decodeMeasures(staff) {
    var measures = [], curClef = staff.clef;
    for (var mi = 0; mi < staff.records.length; mi++) {
        var rec = staff.records[mi], slots = [], measureAcc = {}, oc = 0;
        for (var e = 0; e < rec.entries.length; e++) {
            var b0 = rec.entries[e][0], b1 = rec.entries[e][1];
            var sym = symbolOf(b0), v = vertical(b0, b1), s, n;

            if (sym === CLEF_TREBLE || sym === CLEF_BASS) { curClef = sym; continue; }
            if (sym === SYM_OCTAVA) { oc = 1; continue; }

            if (sym === SYM_TIE || sym === SYM_TIE_BELOW) {
                // The engine's handler picks ONE note by searching from the glyph's v.
                for (var t = slots.length - 1; t >= 0; t--) {
                    if (slots[t].isRest) continue;
                    var best = slots[t].notes[0];
                    for (n = 1; n < slots[t].notes.length; n++) {
                        if (Math.abs(slots[t].notes[n][2] - v) < Math.abs(best[2] - v)) best = slots[t].notes[n];
                    }
                    best[3] = true;
                    break;
                }
                continue;
            }
            if (sym === SYM_NATURAL || sym === SYM_SHARP || sym === SYM_FLAT) {
                measureAcc[v] = sym === SYM_SHARP ? 2 : (sym === SYM_FLAT ? -2 : 0x0C);
                continue;
            }
            if (sym === SYM_DOT) {
                // Dots the voice sitting exactly at the glyph's v, and refuses a 32nd.
                for (var dd = slots.length - 1; dd >= 0; dd--) {
                    if (slots[dd].isRest) continue;
                    var minDur = Infinity;
                    for (n = 0; n < slots[dd].notes.length; n++) {
                        var note = slots[dd].notes[n];
                        if (note[2] === v && note[1] >= 2) note[1] += Math.floor(note[1] / 2);
                        if (note[1] < minDur) minDur = note[1];
                    }
                    slots[dd].advance = minDur;
                    break;
                }
                continue;
            }

            var nv = noteValue(sym);
            if (!nv[0]) continue;                      // markers / unknown: no time
            var dur = NOTE_TICKS[nv[1]];
            if (nv[0] === 'rest') {
                slots.push({ slotX: xSlot(b1), advance: dur, isRest: true, notes: [], octave: 0 });
                continue;
            }
            var acc2 = (v in measureAcc) ? measureAcc[v] : staff.keysig[(v - 1) % 7];
            if (acc2 === 0x0C) acc2 = 0;               // forced natural
            var midi = staffMidi(staff, v, acc2, curClef);
            if (midi) midi += 12 * oc;
            var last = slots.length ? slots[slots.length - 1] : null;
            if (last && !last.isRest && xSlot(b1) === last.slotX) {
                last.notes.push([midi, dur, v, false]);        // chord member
                if (dur < last.advance) last.advance = dur;
            } else {
                slots.push({ slotX: xSlot(b1), advance: dur, isRest: false,
                             notes: [[midi, dur, v, false]], octave: oc });
            }
        }
        measures.push(slots);
    }
    return measures;
}

/** Ticks until every voice of the measure has finished — the barline gate. */
function span(slots) {
    var t = 0, end = 0;
    for (var i = 0; i < slots.length; i++) {
        var longest = slots[i].advance;
        for (var n = 0; n < slots[i].notes.length; n++) {
            if (slots[i].notes[n][1] > longest) longest = slots[i].notes[n][1];
        }
        if (t + longest > end) end = t + longest;
        t += slots[i].advance;
    }
    return Math.max(end, t);
}

function fillsMeasure(slots) {
    // A lone whole rest means "rest this whole measure", whatever the meter.
    return slots.length === 1 && slots[0].isRest && slots[0].advance === 32;
}

/**
 * parse(bytes) → { notes, tracks, tickSeconds, durationSeconds }
 * bytes: Uint8Array of the .MCS/.MCD file.
 */
function parse(bytes) {
    var d = bytes;
    if (!d || d.length < 0x0F) throw new Error('mcs_too_short');

    var staffRecs = splitStaves(parseRecords(d));
    if (!staffRecs.length) throw new Error('mcs_no_staves');

    var decoded = [];
    for (var si = 0; si < staffRecs.length; si++) {
        var st = readStaff(staffRecs[si]);
        decoded.push({ staff: st, measures: decodeMeasures(st) });
    }

    /* The measure grid is the MODAL span, not the maximum: one long final bar
       must not stretch every measure of the song. */
    var spans = [];
    for (var q = 0; q < decoded.length; q++) {
        for (var m = 0; m < decoded[q].measures.length; m++) {
            var ms = decoded[q].measures[m];
            if (ms.length && !fillsMeasure(ms)) spans.push(span(ms));
        }
    }
    var measureLen = 32;
    if (spans.length) {
        var freq = {}, bestV = null, bestN = -1;
        for (var f = 0; f < spans.length; f++) freq[spans[f]] = (freq[spans[f]] || 0) + 1;
        for (var key in freq) {
            var val = +key;
            if (freq[key] > bestN || (freq[key] === bestN && val > bestV)) { bestN = freq[key]; bestV = val; }
        }
        measureLen = bestV;
    }
    for (var w = 0; w < decoded.length; w++) {
        for (var wm = 0; wm < decoded[w].measures.length; wm++) {
            if (fillsMeasure(decoded[w].measures[wm])) decoded[w].measures[wm][0].advance = measureLen;
        }
    }

    var nMeas = 0;
    for (var c = 0; c < decoded.length; c++) nMeas = Math.max(nMeas, decoded[c].measures.length);
    var mDur = [], mStart = [0];
    for (var i2 = 0; i2 < nMeas; i2++) {
        var dur = measureLen;
        for (var j2 = 0; j2 < decoded.length; j2++) {
            if (i2 < decoded[j2].measures.length) dur = Math.max(dur, span(decoded[j2].measures[i2]));
        }
        mDur.push(dur);
        mStart.push(mStart[mStart.length - 1] + dur);
    }

    var tickSec = tickSecondsFor(d[0]);
    var notes = [], trackNames = [];
    for (var idx = 0; idx < decoded.length; idx++) {
        trackNames.push(decoded[idx].staff.clef === CLEF_TREBLE ? 'Treble' : 'Bass');
        for (var mi2 = 0; mi2 < decoded[idx].measures.length; mi2++) {
            var slots2 = decoded[idx].measures[mi2];
            var tick = mStart[mi2];
            var deficit = mDur[mi2] - span(slots2);
            if (slots2.length && deficit > 0) {
                // A staff entering a shared measure late is front-padded.
                var otherFirst = null;
                for (var o = 0; o < decoded.length; o++) {
                    if (o === idx || mi2 >= decoded[o].measures.length) continue;
                    var om = decoded[o].measures[mi2];
                    if (!om.length) continue;
                    if (otherFirst === null || om[0].slotX < otherFirst) otherFirst = om[0].slotX;
                }
                if (otherFirst !== null && slots2[0].slotX > otherFirst) tick += deficit;
            }
            for (var s2 = 0; s2 < slots2.length; s2++) {
                var slot = slots2[s2];
                if (!slot.isRest) {
                    for (var n2 = 0; n2 < slot.notes.length; n2++) {
                        var nn = slot.notes[n2];
                        if (!nn[0]) continue;              // unusable staff position
                        notes.push({
                            startTimeSeconds: tick * tickSec,
                            durationSeconds: nn[1] * tickSec,
                            pitchMidi: nn[0],
                            amplitude: 0.8,
                            _track: idx,
                            _startTick: tick,
                            _durTicks: nn[1],
                        });
                    }
                }
                tick += slot.advance;
            }
        }
    }
    notes.sort(function (a, b) {
        return (a.startTimeSeconds - b.startTimeSeconds) || (a.pitchMidi - b.pitchMidi);
    });

    var end = 0;
    for (var z = 0; z < notes.length; z++) {
        var e2 = notes[z].startTimeSeconds + notes[z].durationSeconds;
        if (e2 > end) end = e2;
    }

    return { notes: notes, tracks: trackNames, tickSeconds: tickSec, durationSeconds: end };
}

/** Cheap sniff: MCS has no magic bytes, so go by extension plus a header sanity
 *  check (tempo byte in range and a measure marker early in the body). */
function looksLikeMcs(name, bytes) {
    if (name && /\.(mcs|mcd)$/i.test(name)) return true;
    if (!bytes || bytes.length < 0x20) return false;
    if (bytes[0] < 0x77 || bytes[0] > 0x92) return false;
    for (var i = 0x0F; i < Math.min(bytes.length - 1, 0x40); i++) {
        if (bytes[i] === 0xFF && bytes[i + 1] === 0xFF) return true;
    }
    return false;
}

scope.McsParser = { parse: parse, looksLikeMcs: looksLikeMcs, tickSecondsFor: tickSecondsFor };

})(typeof self !== 'undefined' ? self : this);
